import { SweepMesh } from '../geometry/sweep';
import { worldMetersToPrintMm } from './types';
import { cleanupMeshForStl, triangleNormal } from './meshCleanup';

/**
 * Encode a mesh as binary STL. Positions are world metres; output vertices are
 * print millimetres at the fixed print scale.
 *
 * Cleans the mesh first (facet normals from triangle geometry, drop degenerates)
 * so slicers get a valid facet soup.
 */
export function encodeBinaryStl(mesh: SweepMesh, name = 'part'): Uint8Array {
  const { mesh: cleaned } = cleanupMeshForStl(mesh);
  const triCount = Math.floor(cleaned.indices.length / 3);
  const buf = new ArrayBuffer(84 + triCount * 50);
  const header = new Uint8Array(buf, 0, 80);
  // STL header must be 80 bytes; keep printable ASCII, pad with spaces.
  const label = `RCSim ${name}`.slice(0, 80);
  for (let i = 0; i < 80; i++) header[i] = i < label.length ? label.charCodeAt(i) & 0x7f : 0x20;

  const view = new DataView(buf);
  view.setUint32(80, triCount, true);

  let offset = 84;
  const { positions, indices } = cleaned;
  for (let t = 0; t < triCount; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];

    // Always use geometric facet normal (STL requirement); never vertex normals.
    const n = triangleNormal(positions, i0, i1, i2) ?? ([0, 0, 0] as [number, number, number]);
    view.setFloat32(offset, n[0], true);
    view.setFloat32(offset + 4, n[1], true);
    view.setFloat32(offset + 8, n[2], true);
    offset += 12;

    for (const vi of [i0, i1, i2]) {
      const x = worldMetersToPrintMm(positions[vi * 3]);
      const y = worldMetersToPrintMm(positions[vi * 3 + 1]);
      const z = worldMetersToPrintMm(positions[vi * 3 + 2]);
      view.setFloat32(offset, x, true);
      view.setFloat32(offset + 4, y, true);
      view.setFloat32(offset + 8, z, true);
      offset += 12;
    }
    view.setUint16(offset, 0, true);
    offset += 2;
  }

  return new Uint8Array(buf);
}
